import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import InviteUserPanel from '../components/Admin/InviteUserPanel';
import apiService from '../services/apiService';
import KnowledgeApprovalPanel from './KnowledgeApprovalPanel';

const ROLE_RANK = { viewer:0, analyst:1, employee:1, manager:2, admin:3, superadmin:4 };
const STATUS_COLORS = { active:'#22c55e', pending:'#f59e0b', suspended:'#ef4444', inactive:'#64748b' };

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState({ status:'all', role:'all' });
  const [showInvite, setShowInvite] = useState(false);
  const [activeTab, setActiveTab]   = useState('team');
  const [pendingCount, setPendingCount] = useState(0);

  const canInvite = ROLE_RANK[user?.role] >= ROLE_RANK['manager'];

  const loadUsers = async () => {
    try {
      const data = await apiService.request('/auth/users');
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  useEffect(() => {
    apiService.request('/api/knowledge/pending')
      .then(data => setPendingCount((data.items || []).length))
      .catch(() => {});
  }, []);

  const filtered = users.filter(u => {
    if (filter.status !== 'all' && u.status !== filter.status) return false;
    if (filter.role   !== 'all' && u.role   !== filter.role)   return false;
    return true;
  });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Team Management</h1>
          <p style={styles.sub}>{users.length} total members</p>
        </div>
        {canInvite && (
          <button style={styles.inviteBtn} onClick={() => setShowInvite(!showInvite)}>
            {showInvite ? 'Close' : '+ Invite Member'}
          </button>
        )}
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:24 }}>
        <button onClick={() => setActiveTab('team')} style={{ padding:'8px 20px', borderRadius:6, border:'none', background: activeTab==='team' ? '#6366f1' : '#1e293b', color: activeTab==='team' ? '#fff' : '#94a3b8', fontWeight:600, cursor:'pointer' }}>
          Team Members
        </button>
        <button onClick={() => setActiveTab('approvals')} style={{ padding:'8px 20px', borderRadius:6, border:'none', background: activeTab==='approvals' ? '#6366f1' : '#1e293b', color: activeTab==='approvals' ? '#fff' : '#94a3b8', fontWeight:600, cursor:'pointer' }}>
          Knowledge Queue{pendingCount > 0 && <span style={{ background:'#dc2626', color:'#fff', borderRadius:20, fontSize:11, padding:'1px 7px', marginLeft:6 }}>{pendingCount}</span>}
        </button>
      </div>

      {activeTab === 'team' ? (
        <>
          {showInvite && (
            <div style={{marginBottom:'32px'}}>
              <InviteUserPanel onInvited={() => { loadUsers(); setShowInvite(false); }} />
            </div>
          )}
          <div style={styles.filters}>
            <select style={styles.select} value={filter.status} onChange={e => setFilter({...filter, status:e.target.value})}>
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
            <select style={styles.select} value={filter.role} onChange={e => setFilter({...filter, role:e.target.value})}>
              <option value="all">All Roles</option>
              <option value="employee">Employee</option>
              <option value="analyst">Analyst</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {loading ? (
            <p style={{color:'#94a3b8'}}>Loading...</p>
          ) : (
            <div style={styles.list}>
              {filtered.map(u => (
                <div key={u._id} style={styles.card}>
                  <div style={styles.avatar}>
                    {(u.profile?.firstName?.[0] || u.email[0]).toUpperCase()}
                  </div>
                  <div style={{flex:1}}>
                    <div style={styles.name}>{u.profile?.firstName} {u.profile?.lastName}</div>
                    <div style={styles.email}>{u.email}</div>
                  </div>
                  <div style={styles.role}>{u.role}</div>
                  <div style={{...styles.badge, background: STATUS_COLORS[u.status] + '33', color: STATUS_COLORS[u.status]}}>
                    {u.status}
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <p style={{color:'#64748b',textAlign:'center',padding:'32px'}}>No users match the current filters.</p>
              )}
            </div>
          )}
        </>
      ) : (
        <KnowledgeApprovalPanel />
      )}
    </div>
  );
}

const styles = {
  page:      { padding:'32px', minHeight:'100vh', background:'linear-gradient(135deg,rgba(59,130,246,0.05),rgba(147,51,234,0.05))' },
  header:    { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'24px' },
  title:     { color:'#e2e8f0', fontSize:'28px', fontWeight:'700', margin:0 },
  sub:       { color:'#64748b', fontSize:'14px', marginTop:'4px' },
  inviteBtn: { padding:'12px 24px', background:'linear-gradient(135deg,#6C63FF,#3b82f6)', border:'none', borderRadius:'8px', color:'white', fontSize:'14px', fontWeight:'600', cursor:'pointer' },
  filters:   { display:'flex', gap:'12px', marginBottom:'24px' },
  select:    { padding:'10px 16px', background:'rgba(30,41,59,0.8)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:'8px', color:'#e2e8f0', fontSize:'14px', outline:'none' },
  list:      { display:'flex', flexDirection:'column', gap:'12px' },
  card:      { display:'flex', alignItems:'center', gap:'16px', padding:'16px 20px', background:'rgba(15,23,42,0.8)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'12px' },
  avatar:    { width:'40px', height:'40px', borderRadius:'50%', background:'linear-gradient(135deg,#6C63FF,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:'700', fontSize:'16px', flexShrink:0 },
  name:      { color:'#e2e8f0', fontWeight:'600', fontSize:'15px' },
  email:     { color:'#64748b', fontSize:'13px', marginTop:'2px' },
  role:      { color:'#94a3b8', fontSize:'13px', textTransform:'capitalize', minWidth:'70px', textAlign:'right' },
  badge:     { padding:'4px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:'600', textTransform:'capitalize' },
};
